import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Lock, 
  CreditCard, 
  Globe, 
  Sun, 
  Moon, 
  Loader2, 
  Shield,
  User,
  CheckCircle2,
  Play,
  Eye,
  XCircle,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPaymentPage() {
  const { slug } = useParams();
  const { t, language, changeLanguage, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLink();
  }, [slug]);

  const fetchLink = async () => {
    try {
      const { data } = await axios.get(`${API}/p/${slug}`);
      setLink(data);
    } catch (err) {
      setError("Payment link not found");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    setProcessing(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        payment_link_id: link.id,
        origin_url: window.location.origin,
      });

      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(t("payment.noCheckoutUrl"));
        setProcessing(false);
      }
    } catch (err) {
      console.error("Checkout error:", err);
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "string") {
        setError(detail);
      } else {
        setError(t("payment.checkoutFailed"));
      }
      setProcessing(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border">
          <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
            <Skeleton className="h-8 w-32" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <Skeleton className="h-10 w-10 rounded-xl" />
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-video rounded-2xl" />
            <Skeleton className="h-96 rounded-2xl" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !link) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-border">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
              <Lock className="h-10 w-10 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold mb-3">{t("payment.linkNotFound")}</h1>
            <p className="text-muted-foreground">
              {t("payment.linkNotFoundDesc")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-payment-page">
      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/80 backdrop-blur-xl z-50">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="text-xl font-black tracking-tight bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] bg-clip-text text-transparent dark:from-white dark:to-slate-300">
            Ellalink
          </span>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-xl">
                  <Globe className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`cursor-pointer ${language === lang.code ? "bg-muted" : ""}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-xl">
              {theme === "light" ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 md:py-16">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Product Preview */}
          <div className="relative" data-testid="media-preview-section">
            <div className="aspect-video rounded-2xl overflow-hidden relative shadow-2xl shadow-[#0B1F3A]/20 group">
              {/* Blurred media preview when media exists */}
              {link.media_file_id ? (
                <>
                  {/* Server-side blurred image preview */}
                  {link.media_content_type?.startsWith("image/") && (
                    <img
                      src={`${API}/media-preview/${link.media_file_id}`}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover scale-110"
                      style={{ filter: "blur(8px)" }}
                      data-testid="blurred-image-preview"
                    />
                  )}
                  {/* Video preview - gradient with play icon */}
                  {link.media_content_type?.startsWith("video/") && (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] to-[#1a3a5c]" data-testid="blurred-video-preview">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(58,109,140,0.4),transparent_60%)]" />
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(58,109,140,0.3),transparent_50%)]" />
                      {/* Animated film grain effect */}
                      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E\")" }} />
                    </div>
                  )}
                  {/* Dark overlay gradient for readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/40" />
                  {/* Lock overlay badge */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
                    <div className="bg-white/95 dark:bg-card/95 backdrop-blur-xl rounded-2xl p-5 sm:p-6 text-center shadow-2xl max-w-[240px] transition-transform duration-300 group-hover:scale-[1.02]">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#0B1F3A]/10 dark:bg-white/10 flex items-center justify-center mx-auto mb-3">
                        {link.media_content_type?.startsWith("video/") ? (
                          <Play className="h-6 w-6 sm:h-7 sm:w-7 text-[#0B1F3A] dark:text-white" />
                        ) : (
                          <Eye className="h-6 w-6 sm:h-7 sm:w-7 text-[#0B1F3A] dark:text-white" />
                        )}
                      </div>
                      <p className="font-semibold text-foreground text-sm sm:text-base">{t("payment.premiumContent")}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {t("payment.unlockedAfterPurchase")}
                      </p>
                    </div>
                  </div>
                  {/* Bottom corner lock indicator */}
                  <div className="absolute bottom-3 right-3 bg-black/50 backdrop-blur-sm rounded-lg px-2.5 py-1.5 flex items-center gap-1.5">
                    <Lock className="h-3 w-3 text-white/80" />
                    <span className="text-[10px] font-medium text-white/80 uppercase tracking-wider">
                      {link.media_content_type?.startsWith("video/") ? "Video" : "Photo"}
                    </span>
                  </div>
                </>
              ) : (
                /* Default placeholder when no media file */
                <>
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] to-[#3A6D8C]" />
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="bg-white/95 dark:bg-card/95 backdrop-blur-xl rounded-2xl p-5 sm:p-6 text-center shadow-xl max-w-[240px]">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#0B1F3A]/10 dark:bg-white/10 flex items-center justify-center mx-auto mb-3">
                        <Lock className="h-6 w-6 sm:h-7 sm:w-7 text-[#0B1F3A] dark:text-white" />
                      </div>
                      <p className="font-semibold text-foreground text-sm sm:text-base">{t("payment.premiumContent")}</p>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        {t("payment.unlockedAfterPurchase")}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Trust badges */}
            <div className="flex items-center justify-center gap-4 sm:gap-6 mt-4 sm:mt-6 text-xs sm:text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>{t("payment.securePaymentBadge")}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                <span>{t("payment.instantDelivery")}</span>
              </div>
            </div>
          </div>

          {/* Purchase Card */}
          <Card className="border-border shadow-xl shadow-[#0B1F3A]/5 dark:shadow-white/5">
            <CardContent className="p-4 sm:p-6 md:p-8">
              {/* Creator Info */}
              {link.creator && (
                <div className="flex items-center gap-4 mb-8 pb-6 border-b border-border">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0B1F3A] to-[#3A6D8C] flex items-center justify-center text-white font-bold text-lg shadow-lg">
                    {link.creator.name?.charAt(0).toUpperCase() || "C"}
                  </div>
                  <div>
                    <p className="font-semibold text-lg">{link.creator.name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {t("payment.creator")}
                    </p>
                  </div>
                </div>
              )}

              {/* Product Info */}
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3">{link.title}</h1>
              {link.description && (
                <p className="text-muted-foreground mb-8 leading-relaxed">{link.description}</p>
              )}

              {/* Price Breakdown - Shows base + 10% commission + total */}
              {(() => {
                const basePrice = link.price;
                const commission = basePrice * 0.10;
                const customerPrice = basePrice + commission;
                return (
                  <div className="bg-gradient-to-r from-[#0B1F3A]/5 to-[#3A6D8C]/5 dark:from-white/5 dark:to-white/10 rounded-2xl p-5 sm:p-6 mb-8" data-testid="checkout-price-breakdown">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{t("payment.mediaPrice")}</span>
                        <span className="font-medium">{formatCurrency(basePrice, link.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{t("payment.platformCommission")}</span>
                        <span className="font-medium">{formatCurrency(commission, link.currency)}</span>
                      </div>
                      <div className="border-t border-border my-2" />
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-base">{t("payment.totalToPay")}</span>
                        <span className="text-2xl sm:text-3xl font-black tracking-tight">
                          {formatCurrency(customerPrice, link.currency)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4 leading-relaxed flex items-start gap-2">
                      <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0 text-emerald-500" />
                      {t("payment.commissionNote")}
                    </p>
                  </div>
                );
              })()}

              {/* Error message with retry */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-4 flex items-start gap-3" data-testid="checkout-error">
                  <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
                  </div>
                </div>
              )}

              {/* Purchase Button */}
              <Button
                className="w-full h-14 text-lg font-semibold rounded-xl shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0"
                onClick={handlePurchase}
                disabled={processing}
                data-testid="purchase-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t("payment.processing")}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    {link.button_text || t("payment.pay")} {formatCurrency(link.price * 1.10, link.currency)}
                  </>
                )}
              </Button>

              {/* Payment Methods */}
              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-xs text-center text-muted-foreground mb-4">
                  {t("payment.securePayment")}
                </p>
                <div className="flex items-center justify-center gap-3">
                  <div className="h-8 px-3 rounded-lg bg-muted flex items-center justify-center">
                    <span className="text-xs font-semibold">VISA</span>
                  </div>
                  <div className="h-8 px-3 rounded-lg bg-muted flex items-center justify-center">
                    <span className="text-xs font-semibold">Mastercard</span>
                  </div>
                  <div className="h-8 px-3 rounded-lg bg-muted flex items-center justify-center">
                    <span className="text-xs font-semibold">Apple Pay</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-sm text-muted-foreground">
          {t("store.poweredBy")} <span className="font-bold text-foreground">Ellalink</span> • {t("store.securePayments")}
        </div>
      </footer>
    </div>
  );
}
